import { Controller, useForm } from 'react-hook-form';
import { CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { TableCell, TableRow } from '@/components/ui/table';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox';
import { useGetRosterForIncident } from 'hooks/incident/useRoster';
import { FocusHighlightCard } from './FocusHighlightCard';
import { useSectionChiefResolver } from '../useSectionChiefResolver';
import type { SectionProps } from './types';
import {
  EditableTable,
  EditableTableCell,
  EditableTableRowActions,
  editableRowClass,
  type EditableTableRowActionState,
} from '@/components/ui/data-table/editable-table';

type OperationsRow = {
  id: string;
  position: string;
  name: string;
  contact: string;
};

function EditableOperationsRow({
  operation,
  actions,
  canEditCards,
  onSave,
  rosterMemberNames,
  rosterMemberLabels,
}: {
  operation: OperationsRow;
  actions: EditableTableRowActionState;
  canEditCards: boolean;
  onSave: (operation: OperationsRow) => void;
  rosterMemberNames: string[];
  rosterMemberLabels: Map<string, string>;
}) {
  const { isEditing } = actions;
  const { register, control, handleSubmit, reset } = useForm<OperationsRow>({
    defaultValues: operation,
  });

  const handleSave = handleSubmit((values) => {
    onSave(values);
    actions.finishEdit();
  });

  return (
    <TableRow
      onClick={isEditing ? undefined : actions.startEdit}
      className={editableRowClass(canEditCards, isEditing)}
    >
      <TableCell>{operation.position}</TableCell>
      <EditableTableCell isEditing={isEditing} displayValue={operation.name}>
        <Controller
          control={control}
          name="name"
          render={({ field }) => (
            <Combobox
              items={rosterMemberNames}
              value={field.value}
              inputValue={field.value}
              onValueChange={(value) => field.onChange(value ?? '')}
              onInputValueChange={(value) => field.onChange(value)}
              disabled={!canEditCards}
            >
              <ComboboxInput placeholder="Select a roster member" showClear />
              <ComboboxContent>
                <ComboboxEmpty>No active roster members found.</ComboboxEmpty>
                <ComboboxList>
                  {(memberName) => (
                    <ComboboxItem key={memberName} value={memberName}>
                      {rosterMemberLabels.get(memberName) ?? memberName}
                    </ComboboxItem>
                  )}
                </ComboboxList>
              </ComboboxContent>
            </Combobox>
          )}
        />
      </EditableTableCell>
      <EditableTableCell isEditing={isEditing} displayValue={operation.contact}>
        <Input {...register('contact')} />
      </EditableTableCell>
      <TableCell>
        <EditableTableRowActions
          isEditing={isEditing}
          canEdit={canEditCards}
          onSave={handleSave}
          onCancel={() => {
            reset(operation);
            actions.cancelEdit();
          }}
        />
      </TableCell>
    </TableRow>
  );
}

export function CardOperations({
  row,
  upsertWala,
  canEditCards,
}: SectionProps) {
  const { sectionChief } = useSectionChiefResolver(row.original.workGroupId);
  const { data: roster = [] } = useGetRosterForIncident();
  const rosterMemberPositions = new Map<string, Set<string>>();
  roster
    .filter((member) => member.active === 1)
    .forEach((member) => {
      const name = member.userName?.trim() || member.emailAddress?.trim();
      if (!name) return;

      const positions = rosterMemberPositions.get(name) ?? new Set<string>();
      member.incidentPositions?.forEach(({ positionName }) => {
        if (positionName) positions.add(positionName);
      });
      rosterMemberPositions.set(name, positions);
    });
  const rosterMemberNames = Array.from(rosterMemberPositions.keys());
  const rosterMemberLabels = new Map(
    Array.from(rosterMemberPositions, ([name, positions]) => [
      name,
      `${name} (${positions.size ? Array.from(positions).join(', ') : 'Unassigned'})`,
    ])
  );

  const operations: OperationsRow[] = [
    {
      id: 'section-chief',
      position: 'Section Chief',
      name: row.original.sectionChief ?? sectionChief?.personName ?? '',
      contact: row.original.sectionChiefContact ?? '',
    },
    {
      id: 'branch-director',
      position: 'Branch Director',
      name: row.original.branchDirector ?? '',
      contact: row.original.branchDirectorContact ?? '',
    },
    {
      id: 'division-group-supervisor',
      position: 'Division/Group Supervisor',
      name: row.original.divisionGroupSupervisor ?? '',
      contact: row.original.divisionGroupSupervisorContact ?? '',
    },
  ];

  const handleSave = (operation: OperationsRow) => {
    const values: Parameters<typeof upsertWala>[1] = {};

    if (operation.id === 'section-chief') {
      values.sectionChief = operation.name;
      values.sectionChiefContact = operation.contact;
    }
    if (operation.id === 'branch-director') {
      values.branchDirector = operation.name;
      values.branchDirectorContact = operation.contact;
    }
    if (operation.id === 'division-group-supervisor') {
      values.divisionGroupSupervisor = operation.name;
      values.divisionGroupSupervisorContact = operation.contact;
    }
    upsertWala(row, values);
  };

  return (
    <FocusHighlightCard size="sm">
      <CardHeader>
        <CardTitle>
          4. Operations or Intelligence/Inspections Personnel
        </CardTitle>
      </CardHeader>
      <CardContent>
        <EditableTable<OperationsRow>
          data={operations}
          columns={[
            { id: 'position', header: 'Position' },
            { id: 'name', header: 'Name' },
            { id: 'contact', header: 'Contact Information' },
            { id: 'actions', header: '' },
          ]}
          getRowId={(operation) => operation.id}
          canEdit={canEditCards}
          renderRow={(operation, actions) => (
            <EditableOperationsRow
              operation={operation}
              actions={actions}
              canEditCards={canEditCards}
              onSave={handleSave}
              rosterMemberNames={rosterMemberNames}
              rosterMemberLabels={rosterMemberLabels}
            />
          )}
        />
      </CardContent>
    </FocusHighlightCard>
  );
}
